import { Logo } from '@/components/logo';
import { SeoHead } from '@/components/seo-head';
import { ThemedText } from '@/components/themed-text';
import { Ionicons } from '@expo/vector-icons';
import { Link, Stack, router } from 'expo-router';
import React from 'react';
import { Linking, Platform, Pressable, ScrollView, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

const isNative = Platform.OS !== 'web';
const SUPPORT_EMAIL = 'controlapag@controlapag.com.br';

/**
 * Como excluir a conta e o que acontece com os dados. É o link de exclusão
 * do Google Play ("URL para exclusão de contas"): precisa citar o app, os
 * passos e o que é apagado ou mantido, com os prazos. O que está aqui segue
 * `UserService.remove` na API; mudou lá, muda aqui.
 */
export default function DeleteAccountScreen() {
  const insets = useSafeAreaInsets();

  return (
    <View className="flex-1 bg-background">
      <Stack.Screen
        options={{ title: 'Excluir conta', headerShown: !isNative, headerShadowVisible: true }}
      />
      <SeoHead
        title="Excluir sua conta · Controlapag"
        description="Como excluir sua conta da Controlapag pelo app, pelo site ou por e-mail, e quais dados são apagados ou mantidos."
        path="/excluir-conta"
      />

      {isNative ? (
        <View className="border-b border-border bg-card" style={{ paddingTop: insets.top }}>
          <View className="min-h-12 flex-row items-center px-4">
            <Pressable onPress={() => router.back()} className="-ml-2 p-2" hitSlop={8}>
              <Ionicons name="arrow-back" size={24} color="#FD5001" />
            </Pressable>
            <ThemedText className="ml-1 font-semibold text-lg">Excluir conta</ThemedText>
          </View>
        </View>
      ) : null}

      <ScrollView
        keyboardShouldPersistTaps="handled"
        className="flex-1"
        contentContainerStyle={{ paddingBottom: isNative ? insets.bottom + 40 : 40 }}
      >
        <View className={`mx-auto w-full max-w-3xl px-6 ${isNative ? 'py-8' : 'py-12'}`}>
          <View className="mb-10 items-center">
            <Logo size={48} color="tint" />
            <ThemedText
              role="heading"
              aria-level={1}
              className="mt-6 text-center font-bold text-3xl"
            >
              Excluir sua conta na Controlapag
            </ThemedText>
            <ThemedText className="mt-2 text-center text-base opacity-70">
              Vale para o app Controlapag (Android e iPhone) e para o site controlapag.com.br.
            </ThemedText>
          </View>

          <Section title="Como excluir">
            <Steps
              title="Pelo app"
              items={[
                'Abra o app Controlapag e entre na sua conta.',
                'Toque em Menu › Perfil (cliente: Conta).',
                'Role até o fim e toque em Excluir conta.',
                'Confirme. A conta é apagada na hora.',
              ]}
            />
            <Steps
              title="Pelo site, sem instalar o app"
              items={[
                'Entre em www.controlapag.com.br com o mesmo e-mail, telefone ou login do Google/Apple.',
                'Abra o seu Perfil, toque em Excluir conta e confirme.',
              ]}
            />
            <Steps
              title="Sem acesso à conta"
              items={[
                `Mande um e-mail para ${SUPPORT_EMAIL}, do e-mail cadastrado, com o assunto "Excluir conta".`,
                'Confirmamos que a conta é sua e excluímos em até 15 dias.',
              ]}
            />
            <Pressable
              onPress={() =>
                void Linking.openURL(`mailto:${SUPPORT_EMAIL}?subject=Excluir%20conta`)
              }
              className="mt-1 self-start"
            >
              <ThemedText className="font-semibold text-primary">
                Escrever para {SUPPORT_EMAIL}
              </ThemedText>
            </Pressable>
          </Section>

          <Section title="O que é apagado na hora">
            <Bullets
              items={[
                'Conta: nome, e-mail, telefone, senha, foto de perfil e o vínculo com o login do Google ou da Apple.',
                'Negócio: serviços, preços, agenda, horários, clientes, matrículas, contratos, cobranças, despesas e relatórios.',
                'Arquivos: fotos da galeria, logo e capa do perfil público, fotos dos serviços e comprovantes de pagamento anexados.',
                'Perfil público e link de agendamento, que deixam de funcionar.',
                'Conversas com o assistente.',
                'A organização de que você é dono. Quem você convidou continua com a própria conta, só que desvinculado.',
              ]}
            />
          </Section>

          <Section title="O que fica guardado, e por quanto tempo">
            <Bullets
              items={[
                'Registros de acesso (endereço IP, data e hora): 6 meses, como exige o Marco Civil da Internet (Lei 12.965/2014, art. 15).',
                'Registros de pagamento da assinatura e documentos fiscais: 5 anos, pelo prazo da legislação tributária. Ficam também com quem processou o pagamento (Stripe, Apple ou Google), conforme as regras deles.',
                'Registros técnicos de erro: até 90 dias.',
                'Cópias de segurança do banco de dados: substituídas automaticamente em até 30 dias.',
              ]}
            />
            <ThemedText className="text-base leading-relaxed opacity-80">
              Depois desses prazos, esses registros também são apagados. Nada disso é usado para
              outra finalidade.
            </ThemedText>
          </Section>

          <Section title="Assinatura">
            <ThemedText className="text-base leading-relaxed opacity-80">
              Assinatura paga com cartão pelo site é cancelada junto com a conta. Assinatura feita
              pela App Store ou pelo Google Play só a própria loja cancela: antes de excluir,
              cancele em Ajustes › seu nome › Assinaturas (iPhone) ou em Google Play › Pagamentos e
              assinaturas (Android). Excluir a conta não cancela a cobrança dessas lojas.
            </ThemedText>
          </Section>

          <View className="mt-8 items-center border-t border-border pt-8">
            <Link href="/privacidade" asChild>
              <Pressable>
                <ThemedText className="font-medium text-primary">
                  Política de Privacidade
                </ThemedText>
              </Pressable>
            </Link>
          </View>
        </View>
      </ScrollView>
    </View>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <View className="mb-10 gap-3">
      <ThemedText role="heading" aria-level={2} className="font-semibold text-xl text-primary">
        {title}
      </ThemedText>
      {children}
    </View>
  );
}

function Steps({ title, items }: { title: string; items: string[] }) {
  return (
    <View className="gap-2">
      <ThemedText className="font-semibold text-base">{title}</ThemedText>
      {items.map((item, index) => (
        <View key={item} className="flex-row gap-3">
          <ThemedText className="w-5 font-semibold text-base text-primary">{index + 1}.</ThemedText>
          <ThemedText className="flex-1 text-base leading-relaxed opacity-80">{item}</ThemedText>
        </View>
      ))}
    </View>
  );
}

function Bullets({ items }: { items: string[] }) {
  return (
    <View className="gap-2">
      {items.map((item) => (
        <View key={item} className="flex-row gap-3">
          <ThemedText className="text-base text-primary">•</ThemedText>
          <ThemedText className="flex-1 text-base leading-relaxed opacity-80">{item}</ThemedText>
        </View>
      ))}
    </View>
  );
}
